import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { Button } from '../../../Button';
import { Modal, ModalActions, ModalContent } from '../../../Modal';
import React, { useCallback, useMemo } from 'react';
import { InsuranceSection } from '../Settings/types';
import { useDeleteInsurance, useInsurances } from '../hooks/use-insurance.hook';
import { useUserProfileQuery, useUserQuery } from '@uplift/queries';
import { Select } from '../../../Inputs';
import { useFormikContext } from 'formik';
import { DeleteInsuranceReason } from './admin-insurance';
import { getArchiveReasonLabel, InsuranceArchiveReason } from '@uplift/api';

export type AdminDeleteInsuranceModalProps = {
  onClose: () => void;
  deleteInsuranceSection: InsuranceSection;
  onAddNewInsurance: () => void;
  userId: string;
};

const insuranceDeleteReasons = [
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.insuranceIsInactive),
    value: InsuranceArchiveReason.insuranceIsInactive
  },
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.planWasOon),
    value: InsuranceArchiveReason.planWasOon
  },
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.insuranceCardNotMatched),
    value: InsuranceArchiveReason.insuranceCardNotMatched
  },
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.coordinationOfBenefitsIssue),
    value: InsuranceArchiveReason.coordinationOfBenefitsIssue
  },
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.insuranceOonAndSessionsCanceled),
    value: InsuranceArchiveReason.insuranceOonAndSessionsCanceled
  },
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.priorAuthNotObtained),
    value: InsuranceArchiveReason.priorAuthNotObtained
  },
  {
    label: getArchiveReasonLabel(InsuranceArchiveReason.insuranceDoesNotCoverServices),
    value: InsuranceArchiveReason.insuranceDoesNotCoverServices
  }
];


export const AdminDeleteInsuranceModal: React.VFC<AdminDeleteInsuranceModalProps> = ({ onClose, deleteInsuranceSection, onAddNewInsurance, userId }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const { classes: modalClasses } = useModalStyles();
  const { data: insurancesData } = useInsurances(userId);
  const { data: patient } = useUserProfileQuery(userId);
  const { data: user } = useUserQuery(userId);
  const formik = useFormikContext<DeleteInsuranceReason>();

  const { loading, onDeleteInsurance, onDeletePrimaryAndReplaceWithSecondary } = useDeleteInsurance(deleteInsuranceSection, userId, onClose, formik.values.archiveReason);

  const secondaryInsuranceData = insurancesData?.[InsuranceSection.secondaryInsurance];
  const deletePrimaryAndReplace = deleteInsuranceSection === InsuranceSection.primaryInsurance && !!secondaryInsuranceData;
  const deleteSecondary = deleteInsuranceSection === InsuranceSection.secondaryInsurance;
  const deletePrimary = deleteInsuranceSection === InsuranceSection.primaryInsurance && !secondaryInsuranceData;
  const name = (patient?.preferredName || user?.firstName) ?? '';

  const isDisabled = loading || formik.values.archiveReason === undefined;

  const onAddNewInsuranceHandler = useCallback(() => {
    onClose();
    onAddNewInsurance();
  }, [ onAddNewInsurance, onClose ]);

  const content = useMemo(() => {
    let content: React.ReactNode = '';

    if (deletePrimaryAndReplace) {
      content = (
        <Box>
          <Typography>{l10n.accountSettings.insurance.adminRemovingMightChangeCost(name)}&nbsp;</Typography>
          <Typography>{l10n.accountSettings.insurance.adminSelectReason}</Typography>
        </Box>
      );
    } else if (deleteSecondary) {
      content = <Typography>{l10n.accountSettings.insurance.adminRemovingMightChangeCost(name)}</Typography>;
    } else if (deletePrimary) {
      content = <Typography>{l10n.accountSettings.insurance.adminIfDeleteInsurance(name)}</Typography>;
    }

    return (
      <ModalContent>
        {content}
      </ModalContent>
    );
  }, [ deletePrimary, deletePrimaryAndReplace, deleteSecondary, l10n.accountSettings.insurance, name ]);

  const title = deletePrimaryAndReplace
    ? l10n.accountSettings.insurance.deleteAndReplaceInsurance
    : l10n.accountSettings.insurance.deleteInsurance;

  const actions = useMemo(() => {
    const cancelButton = (
      <Button onClick={onClose} disabled={loading}>{l10n.general.cancel}</Button>
    );


    if (deletePrimaryAndReplace) {
      return (
        <ModalActions className={classes.actionsBlock}>
          <Button
            onClick={onDeletePrimaryAndReplaceWithSecondary}
            disabled={isDisabled}
            variant="contained"
            color="secondary"
          >
            {l10n.accountSettings.insurance.useInsurance(secondaryInsuranceData.companyName)}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={isDisabled}
            onClick={onAddNewInsuranceHandler}
          >
            {l10n.accountSettings.insurance.addNewInsurance}
          </Button>
        </ModalActions>
      );
    } else {
      return (
        <ModalActions className={classes.actionsInline}>
          {cancelButton}
          <Button
            loading={loading}
            disabled={isDisabled}
            variant="contained"
            color="error"
            onClick={onDeleteInsurance}
          >
            {l10n.accountSettings.insurance.deleteInsurance}
          </Button>
        </ModalActions>
      );
    }
  }, [ classes, deletePrimaryAndReplace, isDisabled, loading, onAddNewInsuranceHandler, onClose, onDeleteInsurance, onDeletePrimaryAndReplaceWithSecondary, secondaryInsuranceData?.companyName ]);

  return (
    <Modal
      title={title}
      fullWidth
      open
      classes={modalClasses}
      onClose={onClose}
      maxWidth="md"
    >
      {content}
      <ModalContent>
        <Select
          name="archiveReason"
          items={insuranceDeleteReasons}
          label="Reason for deleting insurance"
          color="secondary"
          className={classes.root}
        />
      </ModalContent>
      {actions}
    </Modal>
  );
};

const useModalStyles = makeStyles()({
  paperWidthMd: {
    width: '600px',
    maxWidth: '600px'
  },
});

const useStyles = makeStyles()((theme) => ({
  root: {
    maxWidth: '425px'
  },
  actionsInline: {
    '& > :not(:first-child)': {
      margin: 0
    },
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      '& > button': {
        width: '100%'
      }
    }
  },
  actionsBlock: {
    '& > :not(:first-child)': {
      margin: 0
    },
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
    '& > button': {
      width: '100%',
    },
  },
}));
